/**
 * Issued POs that have not been marked purchased, and that never created a
 * supplier bill (company / project / personal card). ACH POs already sit on
 * the forecast as open AP; this fills the card-PO gap.
 */

import type { PurchaseRequisition, PurchaseSpendClass } from '../types/purchasing';
import {
  cardExpenseCoversSupplierPo,
  issuedSupplierPurchaseOrders,
  findPurchasedSupplierOrder,
} from './purchaseOrderPurchases';
import { resolveSpendClass } from './purchasingSpendClass';
import type { SupplierBill } from './supplierBills';

export type IssuedPurchaseForecastSlice = {
  requisitionId: string;
  amount: number;
  poNumber?: string;
  vendorName: string;
  title: string;
  dealId?: string;
  projectId?: string;
  spendClass: PurchaseSpendClass;
  /** OpEx group when this PO draws from a budget envelope. */
  overheadGroup?: string;
  /**
   * Approved overhead PO with a group and no named overhead line.
   * The forecast folds these into Burn when that group has a budget set.
   */
  inOpexBudget?: boolean;
  dueDate: Date | null;
  previewStoragePath?: string;
  previewFileName?: string;
  previewFileUrl?: string;
};

/**
 * Group key when this open PO should sit inside that month's Burn instead of
 * stacking on top of the envelope. Unset budgets stay on the POs line.
 */
export function opexBudgetGroupForIssuedPurchase(
  row: Pick<IssuedPurchaseForecastSlice, 'inOpexBudget' | 'spendClass' | 'overheadGroup'>,
  budgets: Record<string, number> | undefined,
): string | null {
  if (!row.inOpexBudget || row.spendClass !== 'overhead') return null;
  const group = (row.overheadGroup || '').trim();
  if (!group || !budgets || !Object.prototype.hasOwnProperty.call(budgets, group)) return null;
  const envelope = Number(budgets[group]);
  if (!Number.isFinite(envelope) || envelope < 0) return null;
  return group;
}

/** Per group, only the amount of approved open POs above that month's envelope. */
export function opexBudgetBurnExcessByMonth(
  rows: Array<{ monthKey: string; amount: number; group: string }>,
  budgets: Record<string, number>,
): Map<string, number> {
  const sums: Record<string, Record<string, number>> = {};
  for (const row of rows) {
    const byGroup = sums[row.monthKey] ?? {};
    byGroup[row.group] = (byGroup[row.group] ?? 0) + row.amount;
    sums[row.monthKey] = byGroup;
  }
  const excess = new Map<string, number>();
  for (const monthKey of Object.keys(sums)) {
    const byGroup = sums[monthKey];
    let extra = 0;
    for (const group of Object.keys(byGroup)) {
      extra += Math.max(0, byGroup[group] - (budgets[group] ?? 0));
    }
    if (extra > 0) excess.set(monthKey, extra);
  }
  return excess;
}

/** Quote or invoice on the PO, preferring a PDF. */
export function issuedPurchasePreviewFile(
  attachments: Array<{ name?: string; contentType?: string; storagePath?: string; url?: string }> | undefined,
): { storagePath?: string; fileName?: string; fileUrl?: string } | undefined {
  const files = attachments ?? [];
  const pdf = files.find((file) => /pdf/i.test(file.contentType || '') || /\.pdf$/i.test(file.name || ''));
  const file = pdf || files.find((entry) => entry.storagePath || entry.url);
  if (!file || (!file.storagePath && !file.url)) return undefined;
  return {
    storagePath: file.storagePath || undefined,
    fileName: file.name || undefined,
    fileUrl: file.url || undefined,
  };
}

/** Blank or mixed vendors on one issued PO. A single named store still uses that name. */
export function issuedPurchaseVendorLabel(row: {
  vendorName?: string | null;
  lineItems?: Array<{ vendorName?: string | null }>;
}): string {
  const names: string[] = [];
  const seen: Record<string, true> = {};
  const add = (raw?: string | null) => {
    const name = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen[key]) return;
    seen[key] = true;
    names.push(name);
  };
  for (const line of row.lineItems || []) add(line.vendorName);
  if (names.length > 1) return 'Multiple Suppliers';
  if (names.length === 1) return displayIssuedVendor(names[0]);
  add(row.vendorName);
  if (names.length === 1) return displayIssuedVendor(names[0]);
  return 'Multiple Suppliers';
}

function displayIssuedVendor(name: string): string {
  return name.toLowerCase() === 'supplier' ? 'Multiple Suppliers' : name;
}

const CLOSED_STATUSES = new Set([
  'purchased',
  'cancelled',
  'rejected',
  'returned',
]);

export function isForecastOpenIssuedPurchase(
  row: Pick<PurchaseRequisition, 'status' | 'poNumber'>,
): boolean {
  if (CLOSED_STATUSES.has(row.status)) return false;
  if (row.status === 'approved') return true;
  return Boolean(row.poNumber?.trim());
}

export function purchaseCoveredBySupplierBill(
  requisitionId: string,
  bills: Array<Pick<SupplierBill, 'purchaseRequisitionId' | 'status'>>,
): boolean {
  const id = requisitionId.trim();
  if (!id) return false;
  return bills.some(
    (bill) => bill.purchaseRequisitionId === id && bill.status !== 'void',
  );
}

export type IssuedPurchaseCardExpenseLink = {
  id?: string;
  purchaseRequisitionId?: string | null;
  poNumber?: string | null;
};

/**
 * Open issued card-paid POs double-count with the card charge on the forecast
 * when a qbExpenses row is already linked. Match by requisition id, PO number,
 * or the PO's qbExpenseIds back-link.
 */
export function purchaseCoveredByCardExpense(
  row: Pick<PurchaseRequisition, 'id' | 'poNumber' | 'qbExpenseIds'> & {
    qbExpenseId?: string | null;
  },
  expenses: IssuedPurchaseCardExpenseLink[],
): boolean {
  if (!expenses.length) return false;
  const requisitionId = (row.id || '').trim();
  const poNumber = (row.poNumber || '').trim();
  const linkedExpenseIds = new Set<string>();
  for (const id of row.qbExpenseIds || []) {
    const trimmed = (id || '').trim();
    if (trimmed) linkedExpenseIds.add(trimmed);
  }
  const single = (row.qbExpenseId || '').trim();
  if (single) linkedExpenseIds.add(single);

  return expenses.some((expense) => {
    const expenseId = (expense.id || '').trim();
    if (expenseId && linkedExpenseIds.has(expenseId)) return true;
    const linkedPr = (expense.purchaseRequisitionId || '').trim();
    if (requisitionId && linkedPr && linkedPr === requisitionId) return true;
    const linkedPo = (expense.poNumber || '').trim();
    if (poNumber && linkedPo && linkedPo === poNumber) return true;
    return false;
  });
}

export function buildIssuedPurchaseForecastSlices(args: {
  requisitions: PurchaseRequisition[];
  supplierBills: Array<Pick<SupplierBill, 'purchaseRequisitionId' | 'status'>>;
  /** Optional qbExpenses — when linked to a PO, skip that open issued line. */
  qbExpenses?: IssuedPurchaseCardExpenseLink[];
}): IssuedPurchaseForecastSlice[] {
  const slices: IssuedPurchaseForecastSlice[] = [];
  const expenses = args.qbExpenses || [];
  for (const row of args.requisitions) {
    if (!isForecastOpenIssuedPurchase(row)) continue;
    if (purchaseCoveredBySupplierBill(row.id, args.supplierBills)) continue;
    const supplierOrders = issuedSupplierPurchaseOrders(row);
    const title = (row.title || '').trim() || row.prNumber;
    const preview = issuedPurchasePreviewFile(row.attachments);
    const spendClass = resolveSpendClass(row.costObject);
    const overheadGroup = (row.overheadGroup || row.costObject.overheadGroup || '').trim();
    const overheadExpenseId = (row.overheadExpenseId || row.costObject.overheadExpenseId || '').trim();
    const inOpexBudget = row.status === 'approved'
      && spendClass === 'overhead'
      && Boolean(overheadGroup)
      && !overheadExpenseId;
    const base = {
      requisitionId: row.id,
      title,
      dealId: row.dealId || row.costObject.dealId,
      projectId: row.costObject.projectId,
      spendClass,
      overheadGroup: overheadGroup || undefined,
      inOpexBudget: inOpexBudget || undefined,
      dueDate: row.approvedAt ?? row.createdAt ?? null,
      previewStoragePath: preview?.storagePath,
      previewFileName: preview?.fileName,
      previewFileUrl: preview?.fileUrl,
    };
    if (supplierOrders.length > 1) {
      for (const order of supplierOrders) {
        if (findPurchasedSupplierOrder(row, order.poNumber)) continue;
        if (cardExpenseCoversSupplierPo(row, order.poNumber, expenses)) continue;
        const amount = Math.round(order.amount);
        if (amount <= 0) continue;
        const vendorName = order.vendorName.trim() && order.vendorName.toLowerCase() !== 'supplier'
          ? order.vendorName
          : 'Multiple Suppliers';
        slices.push({
          ...base,
          amount,
          poNumber: order.poNumber,
          vendorName,
        });
      }
      continue;
    }
    if (purchaseCoveredByCardExpense(row, expenses)) continue;
    const amount = Math.round(Number(row.amount) || 0);
    if (amount <= 0) continue;
    slices.push({
      ...base,
      amount,
      poNumber: row.poNumber?.trim() || undefined,
      vendorName: issuedPurchaseVendorLabel(row),
    });
  }
  return slices;
}
